import {
  type UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import {
  isAlreadyProByYouError,
  isProGroupGoneError,
  proInvalidateKeys,
  queryKeys,
} from '@splitfiesta/core';
import type {
  AddProGroupDto,
  CurrentUserDto,
  ProGroupMutationResponseDto,
  ProGroupsListResponseDto,
  SuccessResponseDto,
  UserProDto,
} from '@splitfiesta/shared';

import type { ApiError } from '@/hooks/useApi';
import { useTypedApi } from '@/hooks/useTypedApi';

import { useAuthStore } from '@/stores/authStore';
import { useOfflineStore } from '@/stores/offlineStore';

export function useProGroupsQuery(enabled: boolean) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.proGroups(),
    queryFn: async () => {
      const res =
        (await api.pro.listGroups()) as SuccessResponseDto<ProGroupsListResponseDto>;
      return res.data;
    },
    enabled,
    staleTime: 0,
  });
}

interface WriteContext {
  /** Who was signed in when the mutation started. */
  userId: string | undefined;
}

export function useProGroupMutations(): {
  /** Resolves `null` when the group was already Pro by the caller (a retried POST). */
  add: UseMutationResult<
    ProGroupMutationResponseDto | null,
    ApiError,
    AddProGroupDto,
    WriteContext
  >;
  /** Resolves `null` when the group was already not Pro by the caller (a retried DELETE). */
  remove: UseMutationResult<
    ProGroupMutationResponseDto | null,
    ApiError,
    string,
    WriteContext
  >;
} {
  const api = useTypedApi();
  const queryClient = useQueryClient();
  const updateUser = useAuthStore(state => state.updateUser);

  // A response that lands after an account switch belongs to the old user.
  const writePro = (pro: UserProDto, userId: string | undefined) => {
    if (userId && useAuthStore.getState().user?.id === userId) {
      updateUser({ pro });
    }
  };

  // The lost first response carried the new slots; best effort, the next
  // `/auth/me` catches up otherwise.
  const refreshPro = async (userId: string | undefined) => {
    try {
      const me = (await api.auth.me()) as SuccessResponseDto<CurrentUserDto>;
      if (me.data?.pro) writePro(me.data.pro, userId);
    } catch {
      // Keep the stored value.
    }
  };

  const captureUser = (): WriteContext => ({
    userId: useAuthStore.getState().user?.id,
  });

  const settle = (
    res: ProGroupMutationResponseDto | null,
    _variables: unknown,
    context: WriteContext | undefined
  ) => {
    if (res) writePro(res.user, context?.userId);
    proInvalidateKeys().forEach(queryKey =>
      queryClient.invalidateQueries({ queryKey })
    );
  };

  const add = useMutation<
    ProGroupMutationResponseDto | null,
    ApiError,
    AddProGroupDto,
    WriteContext
  >({
    onMutate: captureUser,
    mutationFn: async body => {
      const { userId } = captureUser();
      try {
        const res = (await api.pro.addGroup(
          body
        )) as SuccessResponseDto<ProGroupMutationResponseDto>;
        return res.data;
      } catch (error) {
        if (!isAlreadyProByYouError(error)) throw error;
        await refreshPro(userId);
        return null;
      }
    },
    onSuccess: (res, body, context) => {
      // Rows the limit refused would otherwise sit failed until edited.
      useOfflineStore
        .getState()
        .requeueGroupLimitFailures(context?.userId, body.groupId);
      settle(res, body, context);
    },
    networkMode: 'always',
  });

  const remove = useMutation<
    ProGroupMutationResponseDto | null,
    ApiError,
    string,
    WriteContext
  >({
    onMutate: captureUser,
    mutationFn: async groupId => {
      const { userId } = captureUser();
      try {
        const res = (await api.pro.removeGroup(
          groupId
        )) as SuccessResponseDto<ProGroupMutationResponseDto>;
        return res.data;
      } catch (error) {
        if (!isProGroupGoneError(error)) throw error;
        await refreshPro(userId);
        return null;
      }
    },
    onSuccess: settle,
    networkMode: 'always',
  });

  return { add, remove };
}
